import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Col, Row } from 'react-styled-flexboxgrid'
import { motion, useAnimation } from 'framer-motion'
import media from 'css-in-js-media'
import { StaticQuery, graphql } from 'gatsby'
import Grid from './Grid'
import { Variables } from '../styles/Variables'
import ContactForm, { GDPR_Notice } from './ContactForm'
import LayoutContext from './LayoutContext'
import { BodyCopy, H2 } from './Typography'
import useCurrentWidth from './windowSize'
import { Label, StyledCheckbox } from './Checkbox'

const SidekickWrapper = styled.div`
    position: fixed;
    width: 100%;
    bottom: 0;
    top: 0;
    left: 100%;
    pointer-events: none;
    z-index: 90;

    ${media('>=desktop')} {
        z-index: 9999;
    }
`

const SidekickOverlay = styled(motion.div)`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    background: rgba(34, 34, 34, 0.4);
    pointer-events: all;
    display: none;
    opacity: 0;
    z-index: 0;
    cursor: pointer;

    ${media('<desktop')} {
        display: none !important;
    }
`

const SidekickBody = styled(motion.div)`
    position: relative;
    z-index: 1;
    pointer-events: all;
    background: ${Variables.color.cocoa};
    will-change: transform;
    box-sizing: border-box;
    color: white;
    height: 100%;
    width: 100%;

    ${media('<desktop')} {
        padding-top: 100px;
        opacity: 0;
        transform: translateX(0);
        display: none;
    }

    ${media('>=desktop')} {
        max-width: 80%;
    }

    ${BodyCopy} {
        color: ${Variables.color.lightWarmGrey};
        ${media('<desktop')} {
            margin-bottom: 40px;
        }
    }

    ${Col} {
        ${media('<desktop')} {
            width: 100%;
        }
        :first-child {
            ${media('>=desktop')} {
                max-width: 321px;
            }
        }
        :last-child {
            ${media('>=desktop')} {
                max-width: 432px;
            }
        }
    }

    ${H2} {
        font-size: 34px;
        color: ${Variables.color.lightWarmGrey};
    }

    ${StyledCheckbox} {
        color: ${Variables.color.lightWarmGrey};
    }

    ${Label} {
        color: ${Variables.color.lightWarmGrey};
    }

    ${GDPR_Notice} {
        color: ${Variables.color.lightWarmGrey};
    }
`

const CloseButton = styled.div`
    position: absolute;
    top: 34px;
    right: 80px;
    cursor: pointer;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    :before {
        content: '';
        background: #fff;
        width: 42px;
        height: 42px;
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 0;
        border-radius: 50px;
        transition: 0.6s transform cubic-bezier(0.3, 0.51, 0, 1.08);

        transform: translate3d(-50%, -50%, 0) scale3d(0, 0, 0);
    }

    svg {
        position: relative;
        z-index: 1;
        vertical-align: top;

        path {
            transition: 0.3s stroke ease-out;

            stroke: white;
        }

        ${media('<desktop')} {
            display: none;
        }
    }

    :hover {
        svg path {
            stroke: #000000;
        }
        :before {
            transform: translate3d(-50%, -50%, 0) scale3d(1, 1, 1);
        }
    }

    ${media('<desktop')} {
        top: 35px;
        right: 25px;
        svg path {
            stroke: white;
        }
        :before {
            transform: translate3d(-50%, -50%, 0) scale3d(0, 0, 0);
        }
    }
`

const FormWrap = styled.div`
    height: 100%;
    ${media('<desktop')} {
        height: 100%;
    }

    ${H2} {
        margin: 0 0 20px;

        ${media('<desktop')} {
            margin-bottom: 30px;
            font-size: 38px;
        }
    }
    ${Grid} {
        ${media('<desktop')} {
            width: 100%;
        }
    }
`

const FormContainer = styled.div`
    height: 100%;
    overflow-x: scroll;
    position: relative;
    padding: 20px 16% 20px 10%;
    box-sizing: border-box;
    display: flex;
    align-items: center;

    ${media('<desktop')} {
        padding: 40px 30px 80px 30px;
        align-items: flex-start;
    }
`

const MobileClose = styled.span`
    font-family: ${Variables.font.title};
    font-size: 12px;
    line-height: 14px;
    text-align: center;
    text-transform: uppercase;
    color: ${Variables.color.lightWarmGrey};
    display: none;

    ${media('<desktop')} {
        display: block;
    }
`

export const ReserveFormContainer: React.FC<{
    data?: object
}> = ({ data }) => {
    const { currentLang, SidebarOpen, setSidebarOpen } = useContext(LayoutContext)
    let isMobile = useCurrentWidth() < 1024 ? true : false

    const formdata = data.allContentfulForm.edges.find((i) => i.node.node_locale == currentLang.langKey).node
    const controls = useAnimation()

    useEffect(() => {
        controls.start(SidebarOpen ? 'active' : 'inactive') // Second should be 'inactive'
    }, [SidebarOpen, controls])

    const sidekickBodyStyles = {
        active: {
            x: '-100%',
            opacity: 1,
            display: 'block',
        },
        inactive: {
            x: 0,
            opacity: 1,
            transitionEnd: { display: 'none' },
        },
    }

    const sidekickBodyStylesMobile = {
        active: {
            opacity: 1,
            display: 'block',
            x: '-100%',
        },
        inactive: {
            x: '-100%',
            opacity: 0,
            transitionEnd: { display: 'none' },
        },
    }

    const sidekickOverlayStyles = {
        active: {
            opacity: 1,
            display: 'block',
        },
        inactive: {
            opacity: 0,
            transitionEnd: { display: 'none' },
        },
    }

    return (
        <SidekickWrapper>
            <SidekickOverlay
                onClick={() => {
                    setSidebarOpen(false)
                }}
                animate={controls}
                variants={sidekickOverlayStyles}
                transition={{
                    duration: 0.3,
                    type: 'tween',
                }}
            />

            <SidekickBody
                animate={controls}
                variants={isMobile ? sidekickBodyStylesMobile : sidekickBodyStyles}
                transition={{ type: 'spring', damping: 20, stiffness: 95 }}
            >
                <CloseButton
                    onClick={() => {
                        SidebarOpen ? setSidebarOpen(false) : setSidebarOpen(true)
                    }}
                >
                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5 1.5L1.5 15.5" stroke="white" strokeMiterlimit="10" strokeLinecap="square" />
                        <path d="M15.5 15.5L1.5 1.5" stroke="white" strokeMiterlimit="10" strokeLinecap="square" />
                    </svg>
                    <MobileClose>close</MobileClose>
                </CloseButton>
                <FormWrap>
                    <FormContainer>
                        <Grid>
                            <Row between="md">
                                <Col md={5}>
                                    <div>
                                        <H2>{formdata.formTitle}</H2>
                                        {formdata.formMicrositeCopy && (
                                            <BodyCopy
                                                dangerouslySetInnerHTML={{
                                                    __html: formdata.formMicrositeCopy.childMarkdownRemark.html,
                                                }}
                                            />
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <ContactForm />
                                </Col>
                            </Row>
                        </Grid>
                    </FormContainer>
                </FormWrap>
            </SidekickBody>
        </SidekickWrapper>
    )
}

const ReserveForm = (props) => (
    <StaticQuery
        query={graphql`
            query formQuery {
                allContentfulForm(filter: { property: { name: { regex: "/pazola/i" } } }) {
                    edges {
                        node {
                            formTitle
                            node_locale
                            formMicrositeCopy {
                                childMarkdownRemark {
                                    html
                                }
                            }
                            property {
                                name
                            }
                        }
                    }
                }
            }
        `}
        render={(data) => <ReserveFormContainer data={data} {...props} />}
    />
)

export default ReserveForm
